import { DeleteIcon } from '@chakra-ui/icons';
import { Badge, Box, Flex, HStack, IconButton, SimpleGrid, useToast } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';

import CustomButton from 'components/CustomButton';
import TableComponent from 'components/TableComponent';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { Column } from 'react-table';
import { deleteRecurrency, findAllRecurrences } from 'services/api.service';
import { maskCurrency } from 'utils/number';
import CreatePlan from './components/CreateRecurrence';
import EditRecurrence from './components/EditRecurrence';
import { RecurrencyPeriodDescription } from './components/recurrency-type';

export default function Planos() {
  const navigate = useNavigate();
  const toast = useToast();
  const { mutateAsync: cancelRecurrence, isLoading: isCancelling } = useMutation(deleteRecurrency);
  const columns = useMemo(
    () =>
      [
        {
          Header: 'Nome',
          accessor: 'description',
        },
        {
          Header: 'Valor',
          accessor: 'amount',
          Cell: ({ value }) => {
            return maskCurrency(value);
          },
        },
        {
          Header: 'Período',
          accessor: 'period',
          Cell: ({ value }) => {
            return RecurrencyPeriodDescription[value];
          },
        },
        {
          Header: 'Saldo',
          accessor: 'balance',
          Cell: ({ value }) => {
            return maskCurrency(value);
          },
        },
        {
          Header: 'Status',
          accessor: 'status',
          Cell: ({ value }: { value: string }) => {
            return value ? <Badge colorScheme="green">Ativo</Badge> : <Badge colorScheme="red">Inativo</Badge>;
          },
        },
        {
          accessor: 'createdAt',
          Header: 'Data',
          Cell: ({ value }) => {
            return DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm:ss');
          },
        },
        {
          Header: 'Ações',
          accessor: 'actions',
        },
      ] as Column[],
    [],
  );

  const handleCancelRecurrence = async (recurrenceId: string) => {
    const confirmed = window.confirm(
      'Cancelar esta recorrência irá inativá-la e cancelar todas as cobranças pendentes. Deseja continuar?',
    );

    if (!confirmed) {
      return;
    }

    try {
      await cancelRecurrence(recurrenceId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['findAllRecurrencys'] }),
        queryClient.invalidateQueries({ queryKey: ['findAllRecurrencesClients'] }),
        queryClient.invalidateQueries({ queryKey: ['findRecurrenceClientsSummary'] }),
      ]);
      toast({
        title: 'Recorrência cancelada',
        description: 'A recorrência foi inativada e as cobranças pendentes foram canceladas.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Erro ao cancelar recorrência',
        description: error?.response?.data?.message || 'Não foi possível cancelar a recorrência.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const actions = (row: any) => (
    <Flex align="center" justify="flex-start" w="100%">
      <EditRecurrence recurrence={row} />
      <IconButton
        aria-label="Apertar"
        rounded="md"
        size="sm"
        ml={2}
        variant="outline"
        icon={<FaEye />}
        onClick={() => navigate(`/access/recurrence/${row.id}`)}
      />
      <IconButton
        aria-label="Cancelar recorrência"
        rounded="md"
        size="sm"
        ml={2}
        colorScheme="red"
        icon={<DeleteIcon />}
        isLoading={isCancelling}
        onClick={() => handleCancelRecurrence(row.id)}
      />
    </Flex>
  );

  return (
    <Box>
      <SimpleGrid
        mb="20px"
        columns={{ sm: 1 }}
        spacing={{ base: '20px', xl: '20px' }}
      >
        <TableComponent
          label="Produtos"
          columnsData={columns}
          right={
            <HStack>
              <CustomButton
                onClick={() => navigate('/access/recurrences')}
                colorScheme="brand"
              >
                Recorrências
              </CustomButton>
              <CreatePlan />
            </HStack>
          }
          queryFn={findAllRecurrences}
          queryKey="findAllRecurrencys"
          actions={actions}
        />
      </SimpleGrid>
    </Box>
  );
}
